import { Injectable } from '@angular/core';
import { DBSchema, IDBPDatabase, openDB } from 'idb';

export interface OfflineScope {
  organisationId: string;
  userId: string;
}

/** Une mutation en attente de synchronisation (création, mise à jour, suppression). */
export interface OutboxEntry {
  id?: number;
  localId: string;
  resource: string;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  targetId?: string;
  path?: string;
  payload: unknown;
  createdAt: number;
  attempts: number;
  lastError?: string;
  status?: 'pending' | 'failed';
  failedAt?: number;
  correlationId?: string;
  organisationId: string;
  userId: string;
  scope: string;
}

export interface CacheEntry {
  key: string;
  resource: string;
  data: unknown;
  fetchedAt: number;
  organisationId: string;
  userId: string;
  scope: string;
}

interface LemDbSchema extends DBSchema {
  outbox: {
    key: number;
    value: OutboxEntry;
    indexes: {
      'by-createdAt': number;
      'by-resource': string;
      'by-scope': string;
      'by-scope-createdAt': [string, number];
    };
  };
  cache: {
    key: string;
    value: CacheEntry;
    indexes: {
      'by-resource': string;
      'by-scope': string;
      'by-scope-resource': [string, string];
    };
  };
  meta: {
    key: string;
    value: { key: string; value: unknown };
  };
}

const DB_NAME = 'lem-offline-db';
const DB_VERSION = 3;
const LEGACY_SCOPE = 'legacy:unassigned';

@Injectable({ providedIn: 'root' })
export class OfflineDbService {
  private dbPromise: Promise<IDBPDatabase<LemDbSchema>> | null = null;

  static scopeKey(scope: OfflineScope): string {
    return `tenant:${scope.organisationId}:user:${scope.userId}`;
  }

  private db(): Promise<IDBPDatabase<LemDbSchema>> {
    if (!this.dbPromise) {
      this.dbPromise = openDB<LemDbSchema>(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, _newVersion, transaction) {
          let outbox;
          if (!db.objectStoreNames.contains('outbox')) {
            outbox = db.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
            outbox.createIndex('by-createdAt', 'createdAt');
            outbox.createIndex('by-resource', 'resource');
          } else {
            outbox = transaction.objectStore('outbox');
          }
          if (!outbox.indexNames.contains('by-scope')) outbox.createIndex('by-scope', 'scope');
          if (!outbox.indexNames.contains('by-scope-createdAt')) outbox.createIndex('by-scope-createdAt', ['scope', 'createdAt']);

          let cache;
          if (!db.objectStoreNames.contains('cache')) {
            cache = db.createObjectStore('cache', { keyPath: 'key' });
            cache.createIndex('by-resource', 'resource');
          } else {
            cache = transaction.objectStore('cache');
          }
          if (!cache.indexNames.contains('by-scope')) cache.createIndex('by-scope', 'scope');
          if (!cache.indexNames.contains('by-scope-resource')) cache.createIndex('by-scope-resource', ['scope', 'resource']);

          if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta', { keyPath: 'key' });

          // Les anciennes opérations n'étaient pas liées à un compte. Elles sont
          // mises en quarantaine et ne seront jamais rejouées sous une autre session.
          if (oldVersion < 3) {
            void outbox.openCursor().then(function migrateOutbox(cursor): Promise<void> | void {
              if (!cursor) return;
              const value = cursor.value as Partial<OutboxEntry>;
              if (!value.scope) {
                value.scope = LEGACY_SCOPE;
                value.organisationId = '';
                value.userId = '';
                value.status = 'failed';
                value.lastError = 'Opération antérieure non attribuée à un compte. Recréation manuelle requise.';
                value.failedAt = Date.now();
                cursor.update(value as OutboxEntry);
              }
              return cursor.continue().then(migrateOutbox);
            });
            void cache.openCursor().then(function migrateCache(cursor): Promise<void> | void {
              if (!cursor) return;
              const value = cursor.value as Partial<CacheEntry>;
              if (!value.scope) {
                value.scope = LEGACY_SCOPE;
                value.organisationId = '';
                value.userId = '';
                cursor.update(value as CacheEntry);
              }
              return cursor.continue().then(migrateCache);
            });
          }
        },
      });
    }
    return this.dbPromise;
  }

  async getCache<T>(key: string): Promise<CacheEntry | undefined> {
    return (await this.db()).get('cache', key);
  }

  async setCache(key: string, resource: string, data: unknown, scope: OfflineScope): Promise<void> {
    await (await this.db()).put('cache', {
      key, resource, data, fetchedAt: Date.now(),
      organisationId: scope.organisationId, userId: scope.userId, scope: OfflineDbService.scopeKey(scope),
    });
  }

  async invalidateResourceCache(resource: string, scope: OfflineScope): Promise<void> {
    const db = await this.db();
    const tx = db.transaction('cache', 'readwrite');
    const keys = await tx.store.index('by-scope-resource').getAllKeys([OfflineDbService.scopeKey(scope), resource]);
    await Promise.all(keys.map((key) => tx.store.delete(key)));
    await tx.done;
  }

  async enqueue(entry: Omit<OutboxEntry, 'id' | 'createdAt' | 'attempts' | 'scope'>): Promise<number> {
    const db = await this.db();
    return db.add('outbox', {
      ...entry,
      scope: OfflineDbService.scopeKey(entry),
      createdAt: Date.now(), attempts: 0, status: 'pending',
    });
  }

  async getPendingMutations(scope: OfflineScope): Promise<OutboxEntry[]> {
    const entries = await (await this.db()).getAllFromIndex('outbox', 'by-scope', OfflineDbService.scopeKey(scope));
    return entries.filter((entry) => entry.status !== 'failed').sort((a, b) => a.createdAt - b.createdAt);
  }

  async countPendingMutations(scope: OfflineScope): Promise<number> {
    return (await this.getPendingMutations(scope)).length;
  }

  async removeMutation(id: number, scope: OfflineScope): Promise<void> {
    const db = await this.db();
    const entry = await db.get('outbox', id);
    if (entry?.scope === OfflineDbService.scopeKey(scope)) await db.delete('outbox', id);
  }

  async touchMutationAttempt(id: number, error: string, scope: OfflineScope): Promise<void> {
    const db = await this.db();
    const entry = await db.get('outbox', id);
    if (entry?.scope === OfflineDbService.scopeKey(scope)) {
      entry.attempts += 1; entry.lastError = error; await db.put('outbox', entry);
    }
  }

  async markMutationFailed(id: number, error: string, scope: OfflineScope): Promise<void> {
    const db = await this.db();
    const entry = await db.get('outbox', id);
    if (!entry || entry.scope !== OfflineDbService.scopeKey(scope)) return;
    entry.status = 'failed'; entry.failedAt = Date.now(); entry.lastError = error; await db.put('outbox', entry);
  }

  async getFailedMutations(scope: OfflineScope): Promise<OutboxEntry[]> {
    const entries = await (await this.db()).getAllFromIndex('outbox', 'by-scope', OfflineDbService.scopeKey(scope));
    return entries.filter((entry) => entry.status === 'failed').sort((a, b) => a.createdAt - b.createdAt);
  }

  async retryMutation(id: number, scope: OfflineScope): Promise<void> {
    const db = await this.db();
    const entry = await db.get('outbox', id);
    if (!entry || entry.scope !== OfflineDbService.scopeKey(scope)) return;
    entry.status = 'pending'; entry.attempts = 0; entry.lastError = undefined; entry.failedAt = undefined; await db.put('outbox', entry);
  }

  async getMeta<T>(key: string): Promise<T | undefined> {
    const record = await (await this.db()).get('meta', key); return record?.value as T | undefined;
  }
  async setMeta(key: string, value: unknown): Promise<void> {
    await (await this.db()).put('meta', { key, value });
  }
}
